import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { 
  HeartPulse, 
  Microscope, 
  Activity,
  Stethoscope,
  Syringe,
  Brain,
  Eye,
  Bone,
  ArrowRight
} from 'lucide-react';

const services = [
  {
    icon: HeartPulse,
    title: 'Cardiovascular',
    description: 'ECG, blood pressure, cholesterol panels',
    size: 'col-span-1 md:col-span-6 lg:col-span-4',
  },
  {
    icon: Microscope,
    title: 'Cancer Detection',
    description: 'Early detection screenings for multiple cancer types',
    size: 'col-span-1 md:col-span-6 lg:col-span-4',
  },
  {
    icon: Activity,
    title: 'Diabetes Screening',
    description: 'Glucose testing and metabolic health assessments',
    size: 'col-span-1 md:col-span-6 lg:col-span-4',
  },
  {
    icon: Stethoscope,
    title: 'Wellness Checkups',
    description: 'Complete physical examinations and vital signs',
    size: 'col-span-1 md:col-span-6 lg:col-span-6',
  },
  {
    icon: Syringe,
    title: 'Vaccination',
    description: 'Flu, COVID-19, and workplace immunization programs',
    size: 'col-span-1 md:col-span-6 lg:col-span-6',
  },
  {
    icon: Brain,
    title: 'Mental Health',
    description: 'Confidential assessments for stress and anxiety',
    size: 'col-span-1 md:col-span-4',
  },
  {
    icon: Eye,
    title: 'Vision & Hearing',
    description: 'Professional eye exams and audiometry',
    size: 'col-span-1 md:col-span-4',
  },
  {
    icon: Bone,
    title: 'Bone Density',
    description: 'DEXA scanning and osteoporosis assessment',
    size: 'col-span-1 md:col-span-4',
  },
];

const process = [
  { step: '01', title: 'Consultation', description: 'Assess needs and design program' },
  { step: '02', title: 'Scheduling', description: 'Coordinate dates and logistics' },
  { step: '03', title: 'Screening', description: 'On-site mobile unit deployment' },
  { step: '04', title: 'Results', description: 'Confidential reports and follow-up' },
];

export const Services = () => {
  return (
    <div className="bg-zinc-950 min-h-screen pt-20">
      {/* Hero */}
      <section className="py-32 relative overflow-hidden" data-testid="services-hero">
        <div className="absolute inset-0 grid-pattern opacity-30" />
        <div className="absolute top-1/2 right-1/4 w-[500px] h-[500px] bg-emerald-500/5 rounded-full blur-[150px]" />
        
        <div className="container mx-auto px-6 max-w-7xl relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl"
          >
            <span className="overline mb-6 block">Services</span>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white leading-[0.95] mb-8 font-['Outfit'] tracking-tighter">
              Comprehensive<br />
              <span className="gradient-text">screening solutions</span>
            </h1>
            <p className="text-lg md:text-xl text-zinc-400 leading-relaxed max-w-2xl">
              From cardiovascular health to cancer detection, our full-spectrum 
              services cover every aspect of preventative care.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Bento Grid */}
      <section className="py-24 border-t border-white/5" data-testid="services-grid">
        <div className="container mx-auto px-6 max-w-7xl">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className={`card-surface rounded-2xl p-8 hover:border-emerald-500/30 border-glow ${service.size}`}
                data-testid={`service-card-${index}`}
              >
                <div className="w-12 h-12 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mb-6">
                  <service.icon className="w-6 h-6 text-emerald-500" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-2 font-['Outfit']">{service.title}</h3>
                <p className="text-zinc-500 text-sm leading-relaxed">{service.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-32 bg-zinc-900/30" data-testid="process-section">
        <div className="container mx-auto px-6 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-16"
          >
            <span className="overline mb-4 block">How It Works</span>
            <h2 className="text-4xl md:text-5xl font-bold text-white font-['Outfit'] tracking-tighter">
              Simple four-step process
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {process.map((step, index) => (
              <motion.div
                key={step.step}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="relative"
                data-testid={`process-step-${index}`}
              >
                <div className="card-surface rounded-2xl p-8">
                  <span className="text-5xl font-bold text-emerald-500/20 font-['JetBrains_Mono']">{step.step}</span>
                  <h3 className="text-lg font-semibold text-white mt-4 mb-2 font-['Outfit']">{step.title}</h3>
                  <p className="text-zinc-500 text-sm">{step.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-32 border-t border-white/5" data-testid="services-cta">
        <div className="container mx-auto px-6 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center max-w-2xl mx-auto"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 font-['Outfit'] tracking-tighter">
              Ready to get started?
            </h2>
            <p className="text-zinc-400 text-lg mb-10">
              Schedule a consultation to discuss your organization's screening needs.
            </p>
            <Link to="/request-screening" data-testid="services-cta-btn">
              <Button className="rounded-full px-8 py-6 text-base font-medium bg-emerald-500 hover:bg-emerald-400 text-black transition-all duration-200 glow-emerald">
                Request Screening
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
